import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiLock, FiCheckCircle, FiXCircle, FiShield, FiEye, FiEyeOff } from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import showToast from '../../components/Toast';
import config from '../../config/frontend.config';
import './ResetPassword.css';

const validationSchema = Yup.object({
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your new password'),
});

const API_BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(4);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (token) {
      checkToken();
    } else {
      navigate('/auth');
    }
  }, [token, navigate]);

  // Redirect after success
  useEffect(() => {
    let timer;
    if (success) {
      timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev === 1) {
            navigate('/auth');
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [success, navigate]);

  const checkToken = async () => {
    try {
      const response = await AxiosInstance.get(`${API_BASE_URL}x/resetpassword/check/${token}`);
      if (response.data.statusCode === 200) {
        setTokenValid(true);
      } else {
        showToast.error('Invalid or expired token.');
        navigate('/auth');
      }
    } catch (error) {
      showToast.error('Invalid or expired token.');
      navigate('/auth');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`${API_BASE_URL}/resetpassword/reset/${token}`, { 
        password: values.password, 
        confirmPassword: values.confirmPassword 
      });
      
      if (response.data.statusCode === 200) {
        showToast.success(response.data.message || 'Password has been reset successfully!');
        setSuccess(true);
      } else {
        showToast.warning(response.data.message || 'Could not reset password.');
      }
    } catch (error) {
      showToast.error(error.response?.data?.message || 'An error occurred.');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };
  
  const handleGoToLogin = () => navigate('/auth');

  if (success) {
    return (
      <div className="reset-password-page">
        <div className="reset-info-panel">
          <div className="background-pattern"></div>
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
          </div>
          <div className="reset-info-content">
            <div className="logo-section">
              <div className="logo-icon">
                <FiShield size={48} />
              </div>
              <h1>MailMaster</h1>
            </div>
            <p className="background-subtitle">Security is our priority. Your account is safe with us.</p>
            <div className="security-features">
              <div className="security-feature">
                <div className="feature-icon">
                  <FiLock size={20} />
                </div>
                <span>Bank-level Security</span>
              </div>
              <div className="security-feature">
                <div className="feature-icon">
                  <FiCheckCircle size={20} />
                </div>
                <span>Instant Delivery</span>
              </div>
              <div className="security-feature">
                <div className="feature-icon">
                  <FiShield size={20} />
                </div>
                <span>Privacy Protected</span>
              </div>
            </div>
          </div>
        </div>
        <div className="reset-form-container">
          <div className="reset-form-wrapper">
            <div className="success-container">
              <div className="success-icon">
                <FiCheckCircle size={48} />
              </div>
              <h3>Password Reset Successful!</h3>
              <p>Your password has been successfully reset. You will be redirected to the login page in {redirectCountdown} seconds.</p>
              <button onClick={handleGoToLogin} className="submit-btn-link">
                Go to Login Now
              </button>
              <div className="redirect-msg">
                Redirecting to login in {redirectCountdown} second{redirectCountdown !== 1 ? 's' : ''}...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-info-panel">
          <div className="background-pattern"></div>
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
          </div>
          <div className="reset-info-content">
            <div className="logo-section">
              <div className="logo-icon">
                <FiXCircle size={48} />
              </div>
              <h1>Invalid Token</h1>
            </div>
            <p className="background-subtitle">Token Expired or Invalid</p>
          </div>
        </div>
        <div className="reset-form-container">
          <div className="reset-form-wrapper">
            <div className="error-container">
              <div className="error-card">
                <div className="error-icon">
                  <FiXCircle size={48} />
                </div>
                <h3>Invalid or Expired Token</h3>
                <p>The password reset link is no longer valid. Please request a new one.</p>
                <Link to="/forgotpassword" className="error-btn">
                  Request New Link
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-info-panel">
        <div className="background-pattern"></div>
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
        <div className="reset-info-content">
          <div className="logo-section">
            <div className="logo-icon">
              <FiShield size={48} />
            </div>
            <h1>MailMaster</h1>
          </div>
          <p className="background-subtitle">Security is our priority. Your account is safe with us.</p>
          <div className="security-features">
            <div className="security-feature">
              <div className="feature-icon">
                <FiLock size={20} />
              </div>
              <span>Bank-level Security</span>
            </div>
            <div className="security-feature">
              <div className="feature-icon">
                <FiCheckCircle size={20} />
              </div>
              <span>Instant Delivery</span>
            </div>
            <div className="security-feature">
              <div className="feature-icon">
                <FiShield size={20} />
              </div>
              <span>Privacy Protected</span>
            </div>
          </div>
        </div>
      </div>

      <div className="reset-form-container">
        <div className="reset-form-wrapper">
          <div className="reset-header">
            <h2>Reset Your Password</h2>
            <p>Create a new, secure password for your account.</p>
          </div>

          <Formik
            initialValues={{ password: '', confirmPassword: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, touched, errors }) => (
              <Form className="reset-form">
                <div className="form-group">
                  <label htmlFor="password">New Password</label>
                  <div className="password-input-wrapper">
                    <FiLock className="input-icon" />
                    <Field
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      id="password"
                      placeholder="Enter new password"
                      className={`password-input ${touched.password && errors.password ? 'error' : ''}`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  <ErrorMessage name="password" component="div" className="field-error" />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <FiLock className="input-icon" />
                    <Field
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      className={`password-input ${touched.confirmPassword && errors.confirmPassword ? 'error' : ''}`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  <ErrorMessage name="confirmPassword" component="div" className="field-error" />
                </div>

                <button 
                  type="submit" 
                  className={`reset-submit-btn ${loading ? 'loading' : ''}`} 
                  disabled={isSubmitting || loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner"></div>
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      <FiLock />
                      Reset Password
                    </>
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword; 